import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackBmiDisqualified } from "@/features/analytics/trackers/bmi-disqualified";
import { trackBmiPageViewed } from "@/features/analytics/trackers/bmi-page-viewed";
import { trackDqErrorShown } from "@/features/analytics/trackers/dq-error-shown";
import { trackBmiGroup } from "@/features/analytics/trackers/patient-profile-captured";
import { calculateBmi } from "@/shared/utils";
import { FunnelSelect, FunnelStep } from "../components";
import { FUNNEL_ROUTE } from "../router/constants";
import { useFunnelStore } from "../stores";

const FEET_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  // ttag disallows expressions inside `t` templates, so precompute the value.
  const feet = i + 4;

  return {
    value: String(feet),
    label: t`${feet} ft`,
  };
});

const INCHES_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i),
  label: t`${i} in`,
}));

const inputClassName =
  "w-full rounded-xl border border-neutral-300 bg-white px-4 py-3.5 text-base text-neutral-900 outline-none placeholder:text-neutral-400 hover:border-neutral-500 focus:border-neutral-500 focus:ring-2 focus:ring-secondary-200 focus:ring-offset-1";

export const Measurements = () => {
  const navigate = useNavigate();
  const { analytics } = useCustomerIoContext();
  const set = useFunnelStore((store) => store.set);
  const [heightFeet, setHeightFeet] = useState(
    useFunnelStore.getState().heightFeet?.toString() ?? "",
  );
  const [heightInches, setHeightInches] = useState(
    useFunnelStore.getState().heightInches?.toString() ?? "",
  );
  const [weight, setWeight] = useState(
    useFunnelStore.getState().initialWeight?.toString() ?? "",
  );
  const [goalWeight, setGoalWeight] = useState(
    useFunnelStore.getState().expectedWeight?.toString() ?? "",
  );
  // The BMI disqualification message only appears after the user clicks Continue.
  const [bmiDisqualified, setBmiDisqualified] = useState(false);

  useEffect(() => {
    trackBmiPageViewed({
      customerIoAnalytics: analytics,
      eventPrefix: EVENT_PREFIX.FUNNEL,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentWeight = Number(weight);
  const goal = Number(goalWeight);
  // Weight loss only: the goal must be strictly below the current weight.
  const showGoalError = currentWeight > 0 && goal > 0 && goal >= currentWeight;

  const bmi =
    heightFeet !== "" && heightInches !== "" && currentWeight > 0
      ? calculateBmi({
          heightFeet: Number(heightFeet),
          heightInches: Number(heightInches),
          weightInPounds: currentWeight,
        })
      : 0;

  // Continue stays clickable even below the minimum BMI — the gate is enforced
  // on submit so we can show the message and fire the DQ events (like the
  // normal onboarding) at that point.
  const isComplete =
    heightFeet !== "" &&
    heightInches !== "" &&
    currentWeight > 0 &&
    goal > 0 &&
    goal < currentWeight;

  const handleSubmit = () => {
    if (!isComplete) {
      return;
    }

    trackBmiGroup(bmi);

    if (bmi < 20) {
      setBmiDisqualified(true);
      trackBmiDisqualified({ customerIoAnalytics: analytics });
      trackDqErrorShown("measurements");
      return;
    }

    set({
      heightFeet: Number(heightFeet),
      heightInches: Number(heightInches),
      initialWeight: Number(weight),
      expectedWeight: Number(goalWeight),
    });
    navigate(FUNNEL_ROUTE.PERSONAL_INFO);
  };

  return (
    <FunnelStep
      title={t`Let's check your eligibility for GLP-1 treatment.`}
      description={t`We'll use these numbers to calculate your BMI and build your treatment profile.`}
      footerNote={t`Your BMI helps our providers determine if GLP-1 medication is appropriate for you.`}
      nextDisabled={!isComplete}
      onSubmit={handleSubmit}
    >
      <p className="mb-6 text-lg font-bold text-primary-950">
        {t`What is your height and weight?`}
      </p>

      <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`Height (FT)`}
          </span>
          <FunnelSelect
            value={heightFeet}
            onChange={(value) => {
              setHeightFeet(value);
              setBmiDisqualified(false);
            }}
            options={FEET_OPTIONS}
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`Height (IN)`}
          </span>
          <FunnelSelect
            value={heightInches}
            onChange={(value) => {
              setHeightInches(value);
              setBmiDisqualified(false);
            }}
            options={INCHES_OPTIONS}
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`Weight`}
          </span>
          <input
            type="number"
            inputMode="numeric"
            placeholder={t`lbs`}
            value={weight}
            onChange={(event) => {
              setWeight(event.target.value);
              setBmiDisqualified(false);
            }}
            className={`${inputClassName} mf-masked mf-listen`}
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`Goal Weight`}
          </span>
          <input
            type="number"
            inputMode="numeric"
            placeholder={t`lbs`}
            value={goalWeight}
            onChange={(event) => setGoalWeight(event.target.value)}
            className={`${inputClassName} mf-masked mf-listen`}
          />
          {showGoalError && (
            <span className="mt-2 block text-sm text-red-700">
              {t`Your goal weight must be lower than your current weight.`}
            </span>
          )}
        </label>
      </div>

      {bmiDisqualified && (
        <p className="mt-4 text-sm font-medium text-red-700">
          {t`Your BMI does not meet the minimum requirement for our weight loss program. Please edit your selection to continue.`}
        </p>
      )}
    </FunnelStep>
  );
};
